import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import {
  formatAppCode,
  getAppCodeColor,
} from "@/v2-backoffice-client/usage/usage.constants"

export type UsageAppSplitSegment = {
  App: string
  Weighted28d: number
}

export type UsageAppSplitBarProps = ComponentProps<"div"> & {
  /** Per-app usage, dominant app first (the profile AppMix order). */
  appMix: UsageAppSplitSegment[]
}

// Compact product-split bar for table cells (UsageAreaBar's visual
// language): one amber/green segment per console generation plus the
// dominant app named next to it. Native `title` carries the exact split so
// hundreds of rows stay cheap. Zero-share apps keep a hairline segment —
// "still licensed, stopped using" must not disappear entirely.
function UsageAppSplitBar(props: UsageAppSplitBarProps) {
  const { appMix, className, ...divProps } = props

  const total = appMix.reduce((sum, app) => sum + app.Weighted28d, 0)

  if (appMix.length === 0) {
    return (
      <span className="text-xs text-muted-foreground" {...divProps}>
        —
      </span>
    )
  }

  const shareOf = (app: UsageAppSplitSegment) =>
    total > 0 ? (app.Weighted28d / total) * 100 : 0
  const label = appMix
    .map((app) => `${formatAppCode(app.App)} ${Math.round(shareOf(app))}%`)
    .join(" · ")
  const dominant = appMix[0]

  return (
    <div
      className={cn("flex min-w-0 items-center gap-2", className)}
      title={label}
      {...divProps}
    >
      <div className="flex h-2 w-16 shrink-0 items-stretch overflow-hidden rounded-full bg-muted">
        {appMix.map((app) => (
          <div
            key={app.App}
            className="min-w-[2px]"
            style={{
              width: `${shareOf(app)}%`,
              backgroundColor: getAppCodeColor(app.App),
            }}
          />
        ))}
      </div>
      <span className="truncate text-xs tabular-nums text-muted-foreground">
        {formatAppCode(dominant.App)}
        {total > 0 && ` ${Math.round(shareOf(dominant))}%`}
      </span>
    </div>
  )
}

export default UsageAppSplitBar
